import { SortDirection } from '@bp/planung-graphql-types';
import { action, makeAutoObservable, observable, runInAction } from 'mobx';
import {
  _ClassesQuery,
  _DeleteLessonsDocument,
  _DeleteTimetableCardsDocument,
  _DeleteTimetableCardsMutation,
  _DeleteTimetableCardsMutationVariables,
  _PeopleQuery,
  _RoomsQuery,
  _SubjectContainersQuery,
  _SubjectsQuery,
  _TimetableCardsDocument,
  _TimetableCardsQuery,
  _TimetableCardsQueryVariables,
  CopyLessonsToTimetableVersionDocument,
  CopyLessonsToTimetableVersionMutation,
  CopyLessonsToTimetableVersionMutationVariables,
  CreateLessonsDocument,
  CreateLessonsMutation,
  CreateLessonsMutationVariables,
  LessonCreateInput,
  MergeLessonsDocument,
  MergeLessonsMutation,
  MergeLessonsMutationVariables,
  TimetableVersionLessonsDocument,
  TimetableVersionLessonsQuery,
  TimetableVersionLessonsQueryVariables,
  UpdateLessonsDocument,
  UpdateLessonsMutation,
  UpdateLessonsMutationVariables,
} from '../../../types/planung-graphql-client-defs';
import { LessonInfo } from '../../../utils/calculateLessonInfos';
import { urqlClient } from '../../../utils/urqlClient';
import { lessonResponseToTableType } from '../utils/lessonResponseToTableType';
import { CardsInfo, CardsInfoLesson, ClassInterfaceType, LessonTableType } from './Table/TimetableVersionLessonsTable';

export type VersionLessonTableType = {
  uuid: string;
  subject: string;
  subjectShortName: string;
  classSort: string;
  teacherSort?: string;
  subjectUuid: string;
  editStatus?: 'unused' | 'inVersion' | 'placedCards' | 'blocked' | 'used';
  cardsInfos?: {
    defaultCards: CardsInfo[][];
    subjectContainerCards: CardsInfo[][];
  };
  classes: Array<
    ClassInterfaceType & {
      groups?: Array<{ uuid: string; shortName?: string; name: string; classFraction?: number }>;
    }
  >;
  classesUuids: string[];
  teachers: {
    uuid: string;
    name?: string | null;
    color?: string | null;
  }[];
  teacherUuids: string[];
  elective: boolean;
  teachingLoadEnabled: boolean;
  timetableEnabled: boolean;
  onlyInTimetableVersion: boolean;
  lessonUnits?: {
    uuid: string;
    duration: number;
    count: number;
    subjectContainer?: { uuid: string; name: string; shortName: string } | null;
  }[];
  lessonInfo?: LessonInfo;
  groupClassNames?: string;
  teachingLoadFactors?: string;
  placedCardsCount?: number | null;
  isSubjectContainerLesson?: boolean;
  curriculumUuid?: string;
  schoolYearUuid?: string;
  rooms?: { uuid: string; name: string }[];
  roomsUuids?: string[];
  cardInfoLesson?: CardsInfoLesson;
};

export class TimetableVersionLessonStore {
  public lessons = new Map<string, VersionLessonTableType>();

  private _teachers: Pick<_PeopleQuery, 'people'>['people'] = [];
  private _classes: Pick<_ClassesQuery, 'classes'>['classes'] = [];
  private _subjects: Pick<_SubjectsQuery, 'subjects'>['subjects'] = [];
  private _subjectContainers: Pick<_SubjectContainersQuery, 'subjectContainers'>['subjectContainers'] = [];
  private _rooms: Pick<_RoomsQuery, 'rooms'>['rooms'] = [];

  constructor() {
    makeAutoObservable(this, {
      lessons: observable.shallow,
      assignLessons: action.bound,
    });
  }

  removeLesson(lessonUuid: string) {
    this.lessons.delete(lessonUuid);
  }

  removeLessons(lessonUuids: string[]) {
    lessonUuids.forEach((uuid) => {
      this.lessons.delete(uuid);
    });
  }

  addLessons(lessons: VersionLessonTableType[]) {
    lessons.forEach((lesson) => {
      this.addLesson(lesson);
    });
  }

  addLesson(lesson: VersionLessonTableType) {
    if (this.lessons.has(lesson.uuid)) {
      this.lessons.set(lesson.uuid, { ...this.lessons.get(lesson.uuid), ...lesson });
    } else {
      this.lessons.set(lesson.uuid, { ...lesson });
    }
  }

  async addLessonsByUuids(lessonUuids: string[]) {
    const versionLesson = await this.lessonRequest({ uuids: lessonUuids });
    if (versionLesson) {
      versionLesson.forEach((lesson) => {
        this.addLesson({
          ...lessonResponseToTableType({
            queryLessonsData: [lesson],
            deputateByTeachersData: [], // TODO: add from backend-resolver
            versionContext: true,
            isTeachingBlock: false,
            subjectsData: this._subjects,
            subjectContainerData: this._subjectContainers,
          })[0],
        });
      });
    }
  }

  setLessons(_lessons: LessonTableType[]) {
    const lessons = _lessons.sort((a, b) => {
      const classSortA = a.classSort || '';
      const classSortB = b.classSort || '';
      return classSortA.localeCompare(classSortB);
    });
    this.lessons.clear();
    lessons.forEach((lesson) => {
      this.lessons.set(lesson.uuid, { ...lesson });
    });
  }

  setBasicData({
    teachers,
    classes,
    subjects,
    rooms,
    subjectContainers,
  }: {
    teachers?: Pick<_PeopleQuery, 'people'>['people'];
    classes?: Pick<_ClassesQuery, 'classes'>['classes'];
    subjects?: Pick<_SubjectsQuery, 'subjects'>['subjects'];
    rooms?: Pick<_RoomsQuery, 'rooms'>['rooms'];
    subjectContainers?: Pick<_SubjectContainersQuery, 'subjectContainers'>['subjectContainers'];
  }) {
    if (teachers) {
      this._teachers = teachers;
    }
    if (classes) {
      this._classes = classes;
    }
    if (subjects) {
      this._subjects = subjects;
    }
    if (rooms) {
      this._rooms = rooms;
    }
    if (subjectContainers) {
      this._subjectContainers = subjectContainers;
    }
  }

  async updateCheckboxes({
    lessonUuid,
    type,
    value,
  }: {
    lessonUuid: string;
    type: 'teachingLoadEnabled' | 'timetableEnabled' | 'elective';
    value: boolean;
  }) {
    const lesson = await this.updateLessonRequest({
      where: { uuid: lessonUuid },
      update: {
        [type]: value,
      },
    });
    runInAction(() => {
      if (lesson && lesson.length > 0) {
        this.lessons.set(lesson[0].uuid, { ...this.lessons.get(lesson[0].uuid), ...lesson[0] });
      }
    });
  }

  async deactivateLesson(lessonUuid: string, versionUuid: string) {
    const versionLesson = await this.lessonRequest({ uuids: [lessonUuid] });
    if (versionLesson) {
      await this.removeLessonFromVersionRequest(lessonUuid, versionUuid);
    }
    runInAction(() => {
      this.removeLesson(lessonUuid);
    });
  }

  async deleteLesson(lessonUuid: string, versionUuid: string) {
    const lessonToDelete = await this.lessonRequest({ uuids: [lessonUuid] });
    if (!lessonToDelete?.length || (lessonToDelete && !lessonToDelete[0].onlyInTimetableVersion)) {
      throw new Error(`Lesson with UUID ${lessonUuid} is not only in the timetable version and cannot be deleted.`);
    }

    const deletedCards = await this.deleteCardsRequest(versionUuid, lessonUuid);
    await this.deleteLessonRequest(lessonUuid);

    runInAction(() => {
      this.removeLesson(lessonUuid);
    });
    return { deletedCards, deletedLesson: lessonToDelete };
  }

  async duplicateLesson(lessonUuid: string, versionUuid: string, organizationUuid: string) {
    const _versionLesson = await this.lessonRequest({ uuids: [lessonUuid] });
    if (_versionLesson) {
      const versionLesson = _versionLesson[0];
      const { data } = await this.getCardsRequest(versionUuid, lessonUuid);
      const cards = data?.timetableCards;
      const newLesson = await this.createLessonsRequest({
        subTitle: versionLesson.subTitle,
        comment: versionLesson.comment,
        teachingLoadEnabled: versionLesson.teachingLoadEnabled ?? false,
        timetableEnabled: versionLesson.timetableEnabled ?? false,
        elective: versionLesson.elective ?? false,
        isClassTrip: versionLesson.isClassTrip ?? false,
        onlyInTimetableVersion: true,
        organization: {
          connect: {
            where: {
              node: {
                uuid: organizationUuid,
              },
            },
          },
        },
        schoolYear: {
          connect: {
            where: {
              node: {
                uuid: versionLesson.schoolYear.uuid,
              },
            },
          },
        },
        subject: {
          Subject: {
            connect: {
              where: {
                node: {
                  uuid: versionLesson.subject?.uuid,
                },
              },
            },
          },
          SubjectContainer: {
            connect: {
              where: {
                node: {
                  uuid: versionLesson.subject?.uuid,
                },
              },
            },
          },
        },
        lessonClasses: {
          create: versionLesson.lessonClassesConnection.edges?.map((edge, index) => ({
            edge: {
              order: index,
            },
            node: {
              class: {
                connect: {
                  where: {
                    node: {
                      uuid: edge.node.class.uuid,
                    },
                  },
                },
              },
              ...(edge.node.groups &&
                edge.node.groups.length > 0 && {
                  groups: {
                    connect: edge.node.groups.map((group) => ({ where: { node: { uuid: group.uuid } } })),
                  },
                }),
            },
          })),
        },
        teachers: {
          connect: versionLesson.teachersConnection.edges.map((edge, index) => {
            return {
              where: {
                node: { uuid: edge.node.uuid },
              },
              edge: {
                order: index,
                description: edge.properties.description,
                present: edge.properties.present,
                teachingLoad: edge.properties.teachingLoad,
                teachingLoadHours: edge.properties.teachingLoadHours,
                writesCertificate: edge.properties.writesCertificate,
              },
            };
          }),
        },
        roomSupply: {
          connect: versionLesson.roomSupplyConnection.edges.map((edge, index) => {
            return {
              where: {
                node: { uuid: edge.node.uuid },
              },
              edge: {
                order: index,
                descriptions: edge.properties.descriptions,
              },
            };
          }),
        },
        versions: {
          connect: [
            {
              where: {
                node: { uuid: versionUuid },
              },
            },
          ],
        },
        timetableCards: {
          create: cards?.map((dc) => {
            return {
              node: {
                locked: false,
                duration: dc.duration,
                timetableVersion: { connect: { where: { node: { uuid: versionUuid } } } },
              },
            };
          }),
        },
      });
      runInAction(() => {
        this.addLesson(newLesson[0]);
      });
    }
  }

  async mergeLessons({
    newCards,
    lessonUuids,
    cardsFromLesson,
    removePlacedCards,
    versionUuid,
    organizationUuid,
  }: {
    lessonUuids: string[];
    cardsFromLesson?: string | null;
    newCards?: { duration: number; count: number }[];
    removePlacedCards?: boolean;
    versionUuid: string;
    organizationUuid: string;
  }) {
    const mergedLessons = await this.mergeLessonsRequest({
      timetableVersion: versionUuid,
      removePlacedCards: removePlacedCards ?? false,
      cardsFromLesson: cardsFromLesson ?? null,
      lessonUuids: lessonUuids,
      newCards: newCards ?? [],
    });
    if (mergedLessons) {
      runInAction(() => {
        this.removeLessons(lessonUuids);
        this.addLesson(mergedLessons[0]);
      });
      return mergedLessons[0];
    }
  }

  async moveLessonToVersion(lessonUuid: string, versionUuid: string, organizationUuid: string) {
    const _versionLesson = await this.lessonRequest({ uuids: [lessonUuid] });

    if (_versionLesson) {
      const newLessons = await this.copyLessonsToTimetableVersionRequest({
        lessonUuids: [lessonUuid],
        timetableVersionUuid: versionUuid,
        organizationUuid,
      });
      if (newLessons) {
        runInAction(() => {
          this.removeLesson(lessonUuid);
          this.addLesson(newLessons[0]);
        });
        return newLessons[0];
      }
    }
    throw new Error(`Lesson with UUID ${lessonUuid} not found`);
  }

  async detachLesson(lessonUuid: string) {
    await this.updateLessonRequest({
      where: { uuid: lessonUuid },
      update: {
        teachers: [{ disconnect: [{}] }],
      },
    });
    runInAction(() => {
      const lesson = this.lessons.get(lessonUuid);
      if (lesson) {
        this.lessons.set(lessonUuid, { ...lesson, teachers: [] });
      }
    });
  }

  async assignLessons(
    lessonUuids: string[],
    teachers: {
      uuid: string;
      name: string | null;
      lastName: string | null;
      color?: string | null;
    }[],
  ) {
    const lessonsData = await this.updateLessonRequest({
      where: { uuid_IN: lessonUuids },
      update: {
        teachers: [
          {
            connect: teachers?.map((teacher, index) => {
              return {
                where: {
                  node: { uuid: teacher.uuid },
                },
                edge: {
                  order: index,
                },
              };
            }),
          },
        ],
      },
    });
    if (lessonsData) {
      runInAction(() => {
        lessonsData.forEach((l) => {
          this.lessons.set(l.uuid, { ...l });
        });
      });
    }
  }

  async removeLessonFromVersionRequest(lessonUuid: string, versionUuid: string) {
    const deletedCards = await this.deleteCardsRequest(versionUuid, lessonUuid);
    const removedLessons = await this.updateLessonRequest({
      where: { uuid: lessonUuid },
      update: {
        versions: [
          {
            disconnect: [
              {
                where: { node: { uuid: versionUuid } },
              },
            ],
          },
        ],
      },
    });
    return { deletedCards, removedLessons };
  }

  async copyLessonsToTimetableVersionRequest(input: CopyLessonsToTimetableVersionMutationVariables) {
    const res = await urqlClient.mutation<
      CopyLessonsToTimetableVersionMutation,
      CopyLessonsToTimetableVersionMutationVariables
    >(CopyLessonsToTimetableVersionDocument, {
      ...input,
    });
    if (res.error || !res.data?.copyLessonsToTimetableVersion) {
      throw new Error(`Failed to copy lessons: ${res.error?.message}`);
    }
    const uuids = res.data.copyLessonsToTimetableVersion.nodeUuids;
    const newLessons = await this.lessonRequest({ uuids });
    if (!newLessons) {
      throw new Error('No lessons found after copying to timetable version');
    }
    return lessonResponseToTableType({
      queryLessonsData: newLessons,
      deputateByTeachersData: [], // TODO: add from backend-resolver
      versionContext: true,
      isTeachingBlock: false,
      subjectsData: this._subjects,
      subjectContainerData: this._subjectContainers,
    });
  }

  async mergeLessonsRequest(input: MergeLessonsMutationVariables) {
    const res = await urqlClient
      .mutation<MergeLessonsMutation, MergeLessonsMutationVariables>(MergeLessonsDocument, {
        ...input,
      })
      .toPromise();
    if (res.error || !res.data?.mergeLessons) {
      throw new Error(`Failed to merge lessons: ${res.error?.message}`);
    }
    const uuids = res.data?.mergeLessons.nodeUuids;
    const newLessons = await this.lessonRequest({ uuids });
    if (!newLessons) {
      throw new Error('No lessons found after copying to timetable version');
    }
    return lessonResponseToTableType({
      queryLessonsData: newLessons,
      deputateByTeachersData: [], // TODO: add from backend-resolver
      versionContext: true,
      isTeachingBlock: false,
      subjectsData: this._subjects,
      subjectContainerData: this._subjectContainers,
    });
  }

  async createLessonsRequest(input: LessonCreateInput) {
    const res = await urqlClient
      .mutation<CreateLessonsMutation, CreateLessonsMutationVariables>(CreateLessonsDocument, {
        input,
      })
      .toPromise();
    if (res.error) {
      throw new Error(`Failed to create lessons: ${res.error.message}`);
    }
    return lessonResponseToTableType({
      queryLessonsData: res.data?.createLessons?.lessons,
      deputateByTeachersData: [], // TODO: add from backend-resolver
      versionContext: true,
      isTeachingBlock: false,
      subjectsData: this._subjects,
      subjectContainerData: this._subjectContainers,
    });
  }

  async updateLessonRequest(variables: UpdateLessonsMutationVariables): Promise<VersionLessonTableType[] | undefined> {
    const response = await urqlClient
      .mutation<UpdateLessonsMutation, UpdateLessonsMutationVariables>(UpdateLessonsDocument, variables)
      .toPromise();
    if (response.error) {
      throw new Error(`Failed to update lessons: ${response.error.message}`);
    }
    const updatedLessons = response.data?.updateLessons?.lessons || [];
    return lessonResponseToTableType({
      queryLessonsData: updatedLessons,
      deputateByTeachersData: [], // TODO: add from backend-resolver
      versionContext: true,
      isTeachingBlock: false,
      subjectsData: this._subjects,
      subjectContainerData: this._subjectContainers,
    });
  }

  convertLessonResponseToTableType(
    data?: Pick<TimetableVersionLessonsQuery, 'lessons'>['lessons'],
  ): VersionLessonTableType[] {
    return lessonResponseToTableType({
      queryLessonsData: data,
      deputateByTeachersData: [], // TODO: add from backend-resolver
      versionContext: true,
      isTeachingBlock: false,
      subjectsData: this._subjects,
      subjectContainerData: this._subjectContainers,
    });
  }

  async lessonRequest({ uuids }: { uuids: string[] }) {
    const { data: queryLessonsData } = await urqlClient
      .query<TimetableVersionLessonsQuery, TimetableVersionLessonsQueryVariables>(
        TimetableVersionLessonsDocument,
        {
          lessonWhere: {
            uuid_IN: uuids,
          },
          options: {
            sort: [
              { classSort: SortDirection.Asc },
              { subjectName: SortDirection.Asc },
              { teachersSort: SortDirection.Asc },
            ],
          },
        },
        {
          requestPolicy: 'network-only',
        },
      )
      .toPromise();
    return queryLessonsData ? queryLessonsData?.lessons : null;
  }

  async deleteLessonRequest(lessonUuid: string) {
    await urqlClient.mutation(_DeleteLessonsDocument, { where: { uuid: lessonUuid } }).toPromise();
    return lessonUuid;
  }

  async deleteCardsRequest(versionUuid: string, lessonUuid: string) {
    const cardsToDelete = await this.getCardsRequest(versionUuid, lessonUuid);
    await urqlClient
      .mutation<_DeleteTimetableCardsMutation, _DeleteTimetableCardsMutationVariables>(_DeleteTimetableCardsDocument, {
        where: { uuid_IN: cardsToDelete.data?.timetableCards?.map((c) => c.uuid) },
      })
      .toPromise();
    return cardsToDelete.data?.timetableCards || [];
  }

  async getCardsRequest(versionUuid: string, lessonUuid: string) {
    return await urqlClient
      .query<_TimetableCardsQuery, _TimetableCardsQueryVariables>(_TimetableCardsDocument, {
        where: {
          timetableVersion: { uuid: versionUuid },
          lesson: { uuid: lessonUuid },
        },
      })
      .toPromise();
  }
}
